import React, { useState, useEffect } from "react";
import * as SubframeCore from "@subframe/core";
import { Button, TextField, DropdownMenu } from "../ui";
import { FeatherChevronDown, FeatherBuilding } from "@subframe/core";
import { CreditCardAccount } from "../services/api";
import { SearchableDropdown } from "../shared/presentation/components";

interface VendorCardPaymentRecoProps {
  currentBankAccountName: string;
  creditCardAccounts: CreditCardAccount[];
  transactionDate: string;
  onToAccountChange: (accountId: string) => void;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
}

const VendorCardPaymentReco: React.FC<VendorCardPaymentRecoProps> = ({
  currentBankAccountName,
  creditCardAccounts,
  transactionDate,
  onToAccountChange,
  onDateChange,
  onReferenceNumberChange,
}) => {
  const [selectedToAccount, setSelectedToAccount] =
    useState<CreditCardAccount | null>(null);
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      // Convert transaction date to YYYY-MM-DD format for input
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  const handleToAccountSelect = (account: CreditCardAccount) => {
    setSelectedToAccount(account);
    onToAccountChange(account.zoho_bankaccountid);
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full items-center gap-4">
        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            From Account
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              value={currentBankAccountName}
              disabled
              placeholder="Current bank account"
            />
          </TextField>
        </div>

        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            To Account (Credit Card) *
          </span>
          <SearchableDropdown
            items={creditCardAccounts}
            loading={false}
            trigger={
              <Button
                className="w-full justify-between"
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                <span className="truncate text-left">
                  {selectedToAccount
                    ? selectedToAccount.bank_name
                    : "Select credit card"}
                </span>
              </Button>
            }
            getSearchableText={(account) => account.bank_name}
            renderItem={(account) => account.bank_name}
            getItemKey={(account) => account.id}
            onSelect={handleToAccountSelect}
            selectedKey={selectedToAccount?.id}
            searchPlaceholder="Search credit cards..."
            emptyMessage="No credit card accounts available"
            noResultsMessage="No credit cards match your search"
            dropdownClassName="min-w-[300px]"
          />
        </div>
      </div>

      <div className="flex w-full items-center gap-4">
        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            Date *
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              type="date"
              value={date}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDateChange(event.target.value)
              }
            />
          </TextField>
        </div>

        <div className="flex flex-col items-start gap-2 flex-1">
          <span className="text-body-bold font-body-bold text-default-font">
            Reference Number
          </span>
          <TextField className="w-full" label="" helpText="">
            <TextField.Input
              placeholder="Enter reference number (optional)"
              value={referenceNumber}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleReferenceNumberChange(event.target.value)
              }
            />
          </TextField>
        </div>
      </div>
    </div>
  );
};

export default VendorCardPaymentReco;
